import React from 'react';
import { connect } from 'react-redux';
import { FormSection, reduxForm, change } from 'redux-form';
import styled from 'styled-components';

import { ContentSet, FlexRow } from 'components/Shared/Scaffolding';
import SettingsGroup, { SettingsHelper } from 'components/Shared/SettingsGroup';
import LocationInput from 'components/Shared/LocationInput';
import { Input, Select, Slider, ButtonSet } from 'components/Shared/Forms';
import { TextButton } from 'components/Shared/Buttons';
import { saveLocation, formName, MAX_ZOOM, MIN_ZOOM } from 'modules/location';
import { required, range, onSubmitFail } from 'utils/validation';
import { changeSize, MAP_DIMENSION_UNIT } from 'modules/size';
import { SETTINGS_GROUP } from 'theme';

const StyledInput = styled(Input)`
    display: block;

    input {
        position: relative;
        width: calc(100% - 4.5em);
        z-index: 1;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
`;

const StyledSelect = styled(Select)`
    position: absolute;
    top: 0;
    right: 0;
    width: calc(4.5em + 1px);

    .Select-control {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    .is-focused {
        position: relative;
        z-index: 1;
    }
`;

const StyledSettingsHelper = styled(SettingsHelper)`
    margin-bottom: -${SETTINGS_GROUP.PADDING.Y};
`;

const LocationForm = ({ handleSubmit, onCancel }) => {
    const units = Object.values(MAP_DIMENSION_UNIT).map((x) => ({
        label: x,
        value: x,
    }));
    return (
        <form onSubmit={handleSubmit}>
            <ContentSet spacing="40px">
                <FormSection name="size">
                    <ContentSet spacing="40px">
                        <SettingsGroup name="Size" description="Enter a size for your map">
                            <FlexRow>
                                <StyledInput name="width" label="Width" type="int" width="50%" required>
                                    <StyledSelect name="widthUnit" options={units} />
                                </StyledInput>
                                <StyledInput name="height" label="Height" type="int" width="50%" required>
                                    <StyledSelect name="heightUnit" options={units} />
                                </StyledInput>
                            </FlexRow>
                            <StyledSettingsHelper highlight="info">
                                <strong>Note:</strong> The actual size of your map may vary depending on the HTML and
                                CSS of your site. Make sure your site can handle the sizing of the map appropriately.
                            </StyledSettingsHelper>
                        </SettingsGroup>
                    </ContentSet>
                </FormSection>
                <FormSection name="location">
                    <ContentSet spacing="40px">
                        <SettingsGroup name="Center Location" description="Search for a location">
                            <FormSection name="center" component={LocationInput} />
                        </SettingsGroup>
                        <SettingsGroup name={`Zoom Level (${MIN_ZOOM}-${MAX_ZOOM})`} description="Enter a zoom level">
                            <Slider name="zoom" min={MIN_ZOOM} max={MAX_ZOOM} />
                        </SettingsGroup>
                    </ContentSet>
                </FormSection>
                <ButtonSet>
                    <TextButton primary type="submit">
                        Apply Changes
                    </TextButton>
                    <TextButton secondary type="button" onClick={onCancel}>
                        Cancel
                    </TextButton>
                </ButtonSet>
            </ContentSet>
        </form>
    );
};

export const setLocation = (location) => change(formName, 'location', location);

const validRange = (unit) => (unit === MAP_DIMENSION_UNIT.PERCENT ? range(1, 100) : range(100, 2000));

const validate = ({ size, location }) => {
    return {
        size: {
            width: size ? validRange(size.widthUnit)(size.width) : null,
            height: size ? validRange(size.heightUnit)(size.height) : null,
        },
        location: {
            center: {
                lat: location && location.center ? required(location.center && location.center.lat) : null,
                lng: location && location.center ? required(location.center && location.center.lng) : null,
            },
        },
    };
};

const onSubmit = (values, dispatch, props) => {
    props.onSizeChange(values.size);
    props.onSaveLocation(values.location);
    props.onClose();
};

const mapStateToProps = (state) => ({
    initialValues: {
        size: state.map.size,
        location: state.map.location,
    },
});

const mapDispatchToProps = {
    onSizeChange: changeSize,
    onSaveLocation: saveLocation,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm({ form: formName, validate, onSubmit, onSubmitFail })(LocationForm));
